// Full-document render of the Oscar thank-you (order confirmation) view:
// PageSkeleton (the React document shell) wrapping the toppers layout -- the
// "Rest easy" intro, the create-account island, the order-number/help content
// title, the site's OrderDetail block (the delivery sections with the
// "Shipping / To:" address + 3-step progress bar, the order lines, the
// order-offer islands, and the "Order Summary" panel -- typed-serialized
// server-side through the thank_you_order_detail hook, the same shape the
// account order-detail page renders), the e-signature / support footer with the
// inline "Chat / Email Us" link, and the "Bill From" biller-address snippet.
// The deferred GA4 dataLayer events (ga_ecom + ga_purchase) and Google Seller
// Ratings survey opt-in (ga_seller) inject after hydration. Header/footer slots
// are omitted so the default site chrome renders.
import React from "react";
import { DataLayerPush } from "tsi-common-react/src/components/DataLayerPush/index";
import { GASeller } from "tsi-common-react/src/components/GASeller/index";
import { t } from "ttag";

import { templates } from "@reactivated";

import { OrderDetail } from "../components/OrderDetail";
import { PageSkeleton } from "../components/PageSkeleton";
import { Template as ChatLink } from "./ChatLinkTemplate";
import { Template as CreateAccount } from "./CreateAccountTemplate";

export const Template = (
    props: templates.ThankYouDocumentTemplate,
): React.JSX.Element => {
    // Bound to locals so the ttag msgid stays "${ esigDate } ${ esigTime }".
    // Interpolating props.order_detail.* directly would bake the prop path into
    // the msgid and orphan the catalog entry on the next shape change.
    const esigDate = props.order_detail?.esig_signed_date ?? "";
    const esigTime = props.order_detail?.esig_signed_time ?? "";
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <div className="thank-you-container l-capped-width u-margin-bottom--xl u-full-width-title">
                <div className="thank-you">
                    <div className="intro">
                        <h1 className="intro__title">
                            Rest easy, <span>{props.first_name}.</span>{" "}
                            We&apos;re working on your order.
                        </h1>
                        <p className="intro__copy">
                            You&apos;ll receive order confirmation emails at
                            each step of your order&apos;s progress, at{" "}
                            <strong>{props.email}</strong>. Check your order
                            status anytime at{" "}
                            <a
                                className="intro__link"
                                href={props.order_list_url}
                            >
                                TempurPedicToppers.com
                            </a>
                            . Thanks for shopping with us.
                        </p>

                        {props.create_account && (
                            <CreateAccount {...props.create_account} />
                        )}
                    </div>

                    <div className="content-title">
                        <h1 className="content-title__headline">
                            ORDER NUMBER: {props.order_number} |{" "}
                            {props.order_date}
                        </h1>
                        <span className="content-title__info">
                            Need Help?{" "}
                            <a href={props.cs_phone_rfc3966}>
                                {props.cs_phone_national}
                            </a>
                        </span>
                    </div>
                </div>

                {/* order_detail is the typed OrderDetailData shape; the
                    shared template types it nullable (the other sites' shapes
                    live in sibling props), but on toppers the
                    thank_you_order_detail hook always populates it, e-signature
                    fields included. */}
                {props.order_detail && (
                    <OrderDetail data={props.order_detail} />
                )}

                <div className="thank-you-esig">
                    {props.order_detail?.has_esignature && (
                        <div className="thank-you-esig__copy">
                            <span className="thank-you-esig__copy--bold">
                                {t`Electronic Signature:`}
                            </span>{" "}
                            {t`Acknowledged & Signed on ${esigDate} ${esigTime}`}
                        </div>
                    )}
                    <div className="thank-you-esig__copy thank-you-esig__copy--inline">
                        Questions or concerns? Talk to a Sleep Expert at{" "}
                        <a
                            className="thank-you-esig__copy--bold"
                            href={props.cs_phone_rfc3966}
                        >
                            {props.cs_phone_national}
                        </a>{" "}
                        <span className="thank-you-esig__copy--conjunction">
                            or
                        </span>{" "}
                        {props.chat_link && <ChatLink {...props.chat_link} />}
                    </div>
                    {/* The "Bill From" biller-address snippet
                        (CheckoutSettings.biller_address_snippet) satisfies a
                        Wells-Fargo requirement. The {% if
                        checkout_settings %} guard was always true (get_solo), so
                        the container div always renders -- empty rich-text when
                        no snippet is configured. */}
                    <div
                        className="thank-you-esig__copy"
                        dangerouslySetInnerHTML={{
                            __html: props.biller_snippet,
                        }}
                    />
                </div>
            </div>
            <DataLayerPush events={props.ga_events} />
            <GASeller surveyData={props.ga_seller_survey_data} />
        </PageSkeleton>
    );
};
